/** The builder's data (`specs/M4-V8-forms.md` §5). */
import { useMutation, useQuery } from '@tanstack/react-query';
import { useOptionalToast } from '@elizmonday/ui';
import { useTRPC, useTRPCClient } from '../../lib/trpc';
import { useBoardInvalidation } from '../boards/hooks';

export function useFormByView(boardId: string | null, viewId: string | null) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.forms.byView.queryOptions({ boardId: boardId ?? '0', viewId: viewId ?? '0' }),
    enabled: boardId !== null && viewId !== null,
    staleTime: 10_000,
  });
}

export function useFormMutations() {
  const client = useTRPCClient();
  const invalidate = useBoardInvalidation();
  const toast = useOptionalToast();
  /*
   * **P-190.** Every builder mutation used to have `onSuccess` and nothing else, so a refusal
   * was silent: *Add content ▸ Name* answered 400, the dialog had already closed, and the user
   * saw a form that simply had not gained a question. A refused builder write is never silent
   * again — whatever the reason, and whichever mutation it was.
   */
  const on = {
    onSuccess: invalidate,
    onError: (error: unknown) =>
      toast(error instanceof Error ? error.message : 'That change could not be saved', { kind: 'negative' }),
  };

  return {
    update: useMutation({
      mutationFn: (input: Parameters<typeof client.forms.update.mutate>[0]) =>
        client.forms.update.mutate(input),
      ...on,
    }),
    addQuestion: useMutation({
      mutationFn: (input: Parameters<typeof client.forms.addQuestion.mutate>[0]) =>
        client.forms.addQuestion.mutate(input),
      ...on,
    }),
    updateQuestion: useMutation({
      mutationFn: (input: Parameters<typeof client.forms.updateQuestion.mutate>[0]) =>
        client.forms.updateQuestion.mutate(input),
      ...on,
    }),
    removeQuestion: useMutation({
      mutationFn: (input: { formId: string; questionId: string }) =>
        client.forms.removeQuestion.mutate(input),
      ...on,
    }),
    reorderQuestions: useMutation({
      mutationFn: (input: { formId: string; order: string[] }) =>
        client.forms.reorderQuestions.mutate(input),
      ...on,
    }),
    setActive: useMutation({
      mutationFn: (input: { formId: string; active: boolean }) =>
        client.forms.setActive.mutate(input),
      ...on,
    }),
    regenerateToken: useMutation({
      mutationFn: (input: { formId: string }) => client.forms.regenerateToken.mutate(input),
      ...on,
    }),
  };
}
